import { Injectable } from "@angular/core";
import { BehaviorSubject, lastValueFrom, Observable, Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Device } from "./device";
import { CryptoService } from "./crypto.service";
import { Router } from "@angular/router";
import { JWTTokenService } from "./app.jwt-token.service";
import { gitTag } from "src/environments/version";
import { ToastrService } from "ngx-toastr";

@Injectable({
  providedIn: "root",
})
export class AppService {
  image: string = "";
  isLoadingMsg: boolean = false;
  spiner = false;

  // selectedWname:any;
  setDefaultVal: any;
  workspaceName: any;

  private apiSubscription: Subscription | null = null;
  constructor(
    private http: HttpClient,
    private cs: CryptoService,
    private router: Router,
    private toastr: ToastrService
  ) { }

  dev: Device | any;
  readonly DeviceKeySuffix = "MEMBER";

  baseurl = environment.baseUrl + "/api";
  opened = true;
  private _errorMsg = new BehaviorSubject("");
  private _successMsg = new BehaviorSubject("");
  private _warnMsg = new BehaviorSubject("");
  chatSessionByUser: any = [];
  agentsByUser: any = [];
  showStaticMsg: any = {};
  takeawayFeatureRestriction: boolean = false;
  showNav: boolean = true;
  history: any[] = [];


  private workspacesSubject = new BehaviorSubject<string[]>([]);
  workspaces$ = this.workspacesSubject.asObservable();

  updateWorkspaces(newList: string[]) {
    this.workspacesSubject.next(newList);
  }


  get successMesssage(): Observable<any> {
    return this._successMsg.asObservable();
  }

  get warnMesssage(): Observable<any> {
    return this._warnMsg.asObservable();
  }

  get errorMessage(): Observable<any> {
    return this._errorMsg.asObservable();
  }

  addSuccessMessage(msg: string) {
    this.toastr.success(msg);
  }

  addErrorMessage(msg: string) {
    this.toastr.error(msg);
  }

  addWarnMessage(msg: string) {
    this.toastr.warning(msg);
  }

  set(key: string, value: string) {
    localStorage.setItem(key, value);
  }

  get(key: string) {
    return localStorage.getItem(key);
  }

  remove(key: string) {
    localStorage.removeItem(key);
  }

  clearLocalStorage() {
    var arr: any = [];
    for (var i = 0; i < localStorage.length; i++) {
      if (
        localStorage.key(i)?.startsWith("xdbc.") ||
        localStorage.key(i)?.startsWith("msal.")
      ) {
        arr.push(localStorage.key(i));
      }
    }

    for (var i = 0; i < arr.length; i++) {
      localStorage.removeItem(arr[i]);
    }
  }

  isSkipMethod(method: string): boolean {
    return ["UserService/Login", "UserService/Logout"].includes(method);
  }

  async postRequest(obj: any, method: string): Promise<any> {
    const rpcUrl = `${this.baseurl}/${method}`;
    // const rpcUrl = `${this.baseurl}/rpc/${method}`;
    var httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    // const signedData = await this.sign(obj);

    const token = this.get("xdbc.accessToken")
    // const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    if (token) {
      // const base64Signature = btoa(String.fromCharCode(...signedData));
      httpOptions = {
        ...httpOptions,
        headers: httpOptions.headers.set('Authorization', `Bearer ${token}`),
      };
    }

    return this.sendPostRequest(obj, rpcUrl, httpOptions);
  }

  async wsPostRequestWithHeaders(
    obj: any,
    method: string,
    apiKey: string,
    signature: string,
  ): Promise<any> {
    try {
      const rpcUrl = `${this.baseurl}/${method}`;

      const httpOptions = {
        headers: new HttpHeaders({
          "Content-Type": "application/json",
          apiKey: apiKey,
          signature: signature,
          Authorization: `Bearer ${this.get('xdbc.accessToken')}`
        }),
      };

      return this.sendPostRequest(obj, rpcUrl, httpOptions);
    } catch (error) {
      console.error("Error in wsPostRequestWithHeaders:", error);
      throw error;
    }
  }

  async wsPostRequest(obj: any, method: string): Promise<any> {
    const rpcUrl = `${this.baseurl}/${method}`;

    var httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return this.sendPostRequest(obj, rpcUrl, httpOptions);
  }

  async postRequestWithoutSignature(
    obj: any,
    rpcUrl: string,
    httpOptions: any
  ): Promise<any> {
    return this.sendPostRequest(obj, rpcUrl, httpOptions);
  }

  private currentSubscription: Subscription | null = null;

  private async sendPostRequest(
    obj: any,
    rpcUrl: string,
    httpOptions: any
  ): Promise<any> {
    if (rpcUrl.endsWith("ProcessInstruction")) {
      return new Promise((resolve, reject) => {
        this.currentSubscription = this.http
          .post<any>(rpcUrl, obj, httpOptions)
          .subscribe({
            next: (data) => {
              this.currentSubscription = null;
              resolve(data);
            },
            error: (error) => {
              this.currentSubscription = null;
              reject(error);
            },
          });
      });
    } else {
      const res = this.http.post<any>(rpcUrl, obj, httpOptions);
      return lastValueFrom(res);
    }
  }

  cancelRequest() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
      this.currentSubscription = null;
    }
  }

  async getRequest(endpoint: string) {
    const url = `${this.baseurl}/${endpoint}`;

    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return this.sendGetRequest(url, httpOptions);
  }

  private sendGetRequest(url: string, options: any) {
    return this.http.get(url, options).toPromise();
  }

  async sign(data: any): Promise<Uint8Array | null> {
    if (this.get("xdbc.emailId")) {
      let e: any = this.get("xdbc.emailId") + this.DeviceKeySuffix;
      this.dev = await this.cs.GetDevice(e);
      if (this.dev) {
        let signature = await this.cs.Sign(this.dev, data);
        var signPayload = {
          deviceid: this.dev.DeviceId,
          signature: signature,
        };
        var enc = new TextEncoder();
        return enc.encode(JSON.stringify(signPayload));
      }
    }
    return null;
  }

  async wssign(data: any, wsName: any): Promise<Uint8Array | null> {
    this.dev = await this.cs.GetWorkspace(wsName);
    if (this.dev) {
      let signature = await this.cs.WSign(this.dev.KeyPair.privateKey, data);
      var enc = new TextEncoder();
      return enc.encode(JSON.stringify(signature));
    }

    return null;
  }

  async setDevice(dev: any) {
    this.dev = dev;
    this.cs.PersistDevice(this.dev);
  }

  async setWorkSpace(dev: any) {
    this.dev = dev;
    let csp = this.cs.PersistWorkspace(this.dev);
  }

  get TimeZoneName(): string {
    return this.get("selectedTimeZone") || "UTC";
  }

  getBrowserName(): any {
    var sBrowser,
      sUsrAg = navigator.userAgent;
    if (sUsrAg.indexOf("Firefox") > -1) {
      sBrowser = "Firefox";
    } else if (sUsrAg.indexOf("SamsungBrowser") > -1) {
      sBrowser = "Samsung Internet";
    } else if (sUsrAg.indexOf("Opera") > -1 || sUsrAg.indexOf("OPR") > -1) {
      sBrowser = "Opera";
    } else if (sUsrAg.indexOf("Trident") > -1) {
      sBrowser = "Internet Explorer";
    } else if (sUsrAg.indexOf("Edge") > -1) {
      sBrowser = "Edge";
    } else if (sUsrAg.indexOf("Chrome") > -1) {
      sBrowser = "Chrome";
    } else if (sUsrAg.indexOf("Safari") > -1) {
      sBrowser = "Safari";
    } else {
      sBrowser = "unknown";
    }

    return sBrowser;
  }

  getOsName(): any {
    var OSName = "Unknown";
    if (window.navigator.userAgent.indexOf("Windows NT 10.0") != -1)
      OSName = "Windows";
    if (window.navigator.userAgent.indexOf("Windows NT 6.2") != -1)
      OSName = "Windows";
    if (window.navigator.userAgent.indexOf("Windows NT 6.1") != -1)
      OSName = "Windows";
    if (window.navigator.userAgent.indexOf("Windows NT 6.0") != -1)
      OSName = "Windows";
    if (window.navigator.userAgent.indexOf("Windows NT 5.1") != -1)
      OSName = "Windows";
    if (window.navigator.userAgent.indexOf("Windows NT 5.0") != -1)
      OSName = "Windows";
    if (window.navigator.userAgent.indexOf("Mac") != -1) OSName = "Mac/iOS";
    if (window.navigator.userAgent.indexOf("X11") != -1) OSName = "UNIX";
    if (window.navigator.userAgent.indexOf("Linux") != -1) OSName = "Linux";
    return OSName;
  }

  removeDevice(user: string) {
    this.cs.RemoveDevice(user + this.DeviceKeySuffix);
  }


  // async logOut() {
  //   let obj = {
  //     emailId: this.get("askbuddy-enterprise.emailId"),
  //     name: this.get("askbuddy-enterprise.name"),
  //   };
  //   try {
  //     let res: any = await this.postRequest(obj, "UserService/Logout");
  //     if (res?.message) {
  //       console.log(res?.message);
  //     }
  //   } catch (error) {
  //     console.log("auth failure", error);
  //   }
  // }

  async logOut() {
    this.remove("xdbc.workspace");
    this.remove("xdbc.emailId");
    this.remove("xdbc.accessToken");
    await this.router.navigate(["/login"]);
  }

  public async calculateSHA256(file: File): Promise<string> {
    const fileBuffer = await file.arrayBuffer();
    const nameBytes = new TextEncoder().encode(file.name + ":");
    const combinedBuffer = new Uint8Array(
      nameBytes.length + fileBuffer.byteLength
    );
    combinedBuffer.set(nameBytes, 0);
    combinedBuffer.set(new Uint8Array(fileBuffer), nameBytes.length);
    const hashBuffer = await crypto.subtle.digest("SHA-256", combinedBuffer);
    const byteArray = new Uint8Array(hashBuffer);
    return Array.from(byteArray)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  async checkAndHandleVersion(response: Response) {
    const serverVersion = response.headers.get("version");
    let uiVersion = this.getFormattedVersion();

    if (serverVersion && uiVersion && serverVersion !== uiVersion) {
      this.addWarnMessage(
        "A new version is available. This page will auto-refresh in 15 seconds."
      );
      setTimeout(() => {
        location.reload();
      }, 15000);
    }
  }

  private getFormattedVersion(): string {
    let count = gitTag.version.split("-").length - 1;
    if (count === 2) {
      return gitTag.version.split("-", 2).join("-");
    } else if (count === 1) {
      return gitTag.version.split("-", 1).join("-");
    }
    return gitTag.version;
  }

  async getKeyPair() {
    this.dev = new Device();
    return this.dev.getKeys();
  }

  // Store private key in crypto database
  async storePrivateKeyInCryptoDB(
    privateKeyPEM: string,
    workspaceName: string,
    keyId: number
  ): Promise<void> {
    try {
      // Create device object
      this.dev = new Device();
      this.dev.WorkSpace = workspaceName;

      // Build key pair from private key
      this.dev.KeyPair = await this.cs.buildKeyPairFromPEMAndBase64(
        privateKeyPEM
      );

      this.dev.keyId = keyId;
      await this.setWorkSpace(this.dev);

      const storedDevice = await this.cs.GetWorkspace(workspaceName);
      if (!storedDevice || !storedDevice.KeyPair) {
        throw new Error("Failed to store private key in crypto database");
      }
    } catch (error: any) {
      throw new Error(
        "Failed to store private key: " + (error.message || "Unknown error")
      );
    }
  }

  convertPEMToBase64(pemKey: string): string {
    return btoa(pemKey);
  }
}
